import React, { useState, useEffect, useRef } from 'react';
import { Star, User, ChevronLeft, ChevronRight, Quote, Image } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cuántas tarjetas se ven a la vez, según el breakpoint de Tailwind usado abajo
// (min-w-full por debajo de md, min-w-[33.333%] de md en adelante).
const getItemsPerView = () => (typeof window !== 'undefined' && window.innerWidth >= 768 ? 3 : 1);

const AllReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [totalReviews, setTotalReviews] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [itemsPerView, setItemsPerView] = useState(getItemsPerView);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);

  // El índice máximo válido es el que deja la última tarjeta pegada al borde derecho —
  // más allá de eso el carrusel se desliza hacia espacio vacío (el bug original).
  const maxIndex = Math.max(0, reviews.length - itemsPerView);

  useEffect(() => {
    loadReviews();
  }, []);

  // Recalcular cuántas tarjetas caben si cambia el tamaño de ventana
  useEffect(() => {
    const handleResize = () => {
      setItemsPerView(getItemsPerView());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Si itemsPerView cambia (o se cargan las reseñas) y el índice actual quedó fuera de rango, ajustarlo
  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [itemsPerView, reviews.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-play carousel — avanza de a una tarjeta, pero nunca más allá de maxIndex
  useEffect(() => {
    if (isAutoPlaying && maxIndex > 0) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
      }, 5000);
    }
    return () => clearInterval(autoPlayRef.current);
  }, [isAutoPlaying, maxIndex]);

  const loadReviews = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/reviews?limit=200`);
      if (res.ok) {
        const data = await res.json();
        setReviews(data.reviews || []);
        setTotalReviews(data.total ?? (data.reviews || []).length);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={14}
          className={i <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}
        />
      ))}
    </div>
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  const goToPrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(Math.min(index, maxIndex));
  };

  if (loading) {
    return (
      <section className="py-8 md:py-12 bg-brand-bone overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-brand-black/5 rounded w-64 mx-auto mb-8"></div>
            <div className="flex gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex-1 h-48 bg-brand-black/5 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (reviews.length === 0) {
    return null;
  }

  return (
    <section className="py-8 md:py-12 bg-brand-bone overflow-hidden" data-testid="all-reviews-section">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <motion.div
          className="text-center mb-6 md:mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-display text-2xl md:text-4xl tracking-tight font-light mb-2 text-brand-black">
            Lo que dicen nuestros clientes
          </h2>
          <div className="flex items-center justify-center gap-2">
            <div className="flex gap-0.5">
              {[1,2,3,4,5].map(i => (
                <Star key={i} size={16} className="text-amber-400 fill-amber-400" />
              ))}
            </div>
            <span className="text-brand-black/50 text-sm">{totalReviews} reseñas verificadas</span>
          </div>
        </motion.div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Arrows - Desktop */}
          {maxIndex > 0 && (
            <>
              <button
                onClick={goToPrev}
                className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-white border border-brand-cobalt/20 text-brand-cobalt hover:bg-brand-cobalt/5 hover:border-brand-cobalt/40 transition-all shadow-luxury"
                aria-label="Anterior"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={goToNext}
                className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-white border border-brand-cobalt/20 text-brand-cobalt hover:bg-brand-cobalt/5 hover:border-brand-cobalt/40 transition-all shadow-luxury"
                aria-label="Siguiente"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Reviews Carousel */}
          <div ref={carouselRef} className="overflow-hidden mx-0 md:mx-8">
            <motion.div
              className="flex"
              animate={{ x: `-${currentIndex * (100 / itemsPerView)}%` }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {reviews.map((review, index) => (
                <div
                  key={review.id}
                  className="min-w-full md:min-w-[33.333%] px-2"
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="h-full"
                  >
                    {/* Altura fija para estandarizar todas las cards */}
                    <div className="brand-card hybrid-glow rounded-2xl p-5 md:p-6 transition-all group relative overflow-hidden flex flex-col"
                      style={{ height: '260px' }}
                    >
                      {/* Decorative Quote */}
                      <div className="absolute top-3 right-3 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Quote size={40} className="text-brand-cobalt" />
                      </div>

                      {/* Stars */}
                      <div className="mb-3 flex-shrink-0">
                        {renderStars(review.rating)}
                      </div>

                      {/* Comment — crece para ocupar el espacio disponible */}
                      <p className="text-brand-black/70 text-sm leading-relaxed flex-1 overflow-hidden italic"
                        style={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >
                        &ldquo;{review.comment}&rdquo;
                      </p>

                      {/* Fotos — solo si hay, no desplazan el autor */}
                      {review.photos && review.photos.length > 0 && (
                        <div className="flex gap-2 mt-2 flex-shrink-0">
                          {review.photos.slice(0, 2).map((photo, idx) => (
                            <a
                              key={idx}
                              href={photo}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <img
                                src={photo}
                                alt={`Foto ${idx + 1}`}
                                className="w-10 h-10 object-cover rounded-lg border border-brand-black/10 hover:border-brand-magenta/40 transition-colors"
                              />
                            </a>
                          ))}
                          {review.photos.length > 2 && (
                            <div className="w-10 h-10 rounded-lg bg-brand-black/5 flex items-center justify-center flex-shrink-0">
                              <span className="text-brand-black/40 text-xs">+{review.photos.length - 2}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Author — siempre al fondo */}
                      <div className="flex items-center gap-3 pt-3 mt-auto border-t border-brand-black/[0.08] flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-cobalt/15 to-brand-magenta/15 flex items-center justify-center flex-shrink-0">
                          <User size={15} className="text-brand-black/50" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-brand-black font-medium text-xs truncate">
                            {review.customer_name || review.author_name || 'Cliente'}
                          </p>
                          <p className="text-brand-black/40 text-[10px] truncate">
                            {review.product ? (
                              <span>Compró <span className="text-brand-magenta">{(review.product.name || review.product.inspired_by_name || '').substring(0, 22)}...</span></span>
                            ) : (
                              formatDate(review.created_at)
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Mobile Navigation */}
          {maxIndex > 0 && (
            <div className="flex md:hidden justify-center gap-2 mt-4">
              <button
                onClick={goToPrev}
                className="p-2 rounded-full bg-white border border-brand-black/10 text-brand-black/60"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={goToNext}
                className="p-2 rounded-full bg-white border border-brand-black/10 text-brand-black/60"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}

          {/* Dots Indicator — un punto por posición real del carrusel, no por reseña */}
          {maxIndex > 0 && (
            <div className="flex justify-center gap-2 mt-6 flex-wrap">
              {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-brand-cobalt w-8'
                      : 'bg-brand-black/20 w-2 hover:bg-brand-black/40'
                  }`}
                  aria-label={`Ir a la posición ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AllReviewsSection;
